import * as React from 'react';
import { Clock3, MapPin, RefreshCw, Route as RouteIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ScheduledBusResultsScheduleEntries } from '@/lib/gql/__generated__/graphql';

type Entry = ScheduledBusResultsScheduleEntries;

export interface ScheduleResultsProps {
  entries: Entry[];
  lastUpdatedAt: string | null;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  compact?: boolean;
}

const displayTime = (value: string | null | undefined) => {
  if (!value) return 'Time not documented';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Time not documented';
  return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
};

const displayDay = (value: string | null | undefined) => {
  if (!value) return '';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }).format(
    date,
  );
};

export function ScheduleResults({
  entries,
  lastUpdatedAt,
  loading,
  error,
  onRetry,
  compact = false,
}: ScheduleResultsProps) {
  const routes = [...new Set(entries.map((entry) => entry.route).filter((value): value is string => Boolean(value)))];
  const directions = [
    ...new Set(entries.map((entry) => entry.direction).filter((value): value is string => Boolean(value))),
  ];
  const stops = [...new Set(entries.map((entry) => entry.stopId).filter((value): value is string => Boolean(value)))];
  const [route, setRoute] = React.useState('all');
  const [direction, setDirection] = React.useState('all');
  const [stop, setStop] = React.useState('all');
  const [serviceDay, setServiceDay] = React.useState('all');
  const serviceDays = [
    ...new Set(entries.map((entry) => entry.serviceDay).filter((value): value is string => Boolean(value))),
  ];
  const selected = entries
    .filter((entry) => route === 'all' || entry.route === route)
    .filter((entry) => direction === 'all' || entry.direction === direction)
    .filter((entry) => stop === 'all' || entry.stopId === stop)
    .filter((entry) => serviceDay === 'all' || entry.serviceDay === serviceDay)
    .filter((entry) => {
      const time = entry.scheduledArrival ? new Date(entry.scheduledArrival).getTime() : Number.NaN;
      return Number.isFinite(time) && time >= Date.now();
    })
    .sort((a, b) => new Date(a.scheduledArrival ?? 0).getTime() - new Date(b.scheduledArrival ?? 0).getTime())
    .slice(0, 3);
  const minutesAway = (value: string | null | undefined) => {
    const timestamp = value ? new Date(value).getTime() : Number.NaN;
    if (!Number.isFinite(timestamp)) return 'Time not documented';
    const minutes = Math.max(0, Math.ceil((timestamp - Date.now()) / 60_000));
    return minutes === 0 ? 'Due now' : `${minutes} min`;
  };

  if (loading)
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        Reading the documented schedule…
      </div>
    );
  if (error)
    return (
      <div className="rounded-xl border border-destructive/40 bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">We couldn’t load the documented departures.</p>
        <Button className="mt-4" variant="outline" onClick={onRetry}>
          <RefreshCw className="size-4" /> Retry
        </Button>
      </div>
    );
  if (!entries.length)
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
        No documented schedule entries are available yet.
      </div>
    );

  return (
    <section aria-label="Scheduled departures" className="space-y-5">
      {!compact && (
        <div className="grid gap-3 sm:grid-cols-4">
          <Select
            value={route}
            onValueChange={(value) => setRoute(typeof value === 'string' ? value : 'all')}
            items={[{ value: 'all', label: 'All routes' }, ...routes.map((value) => ({ value, label: value }))]}
          >
            <SelectTrigger aria-label="Route">
              <SelectValue placeholder="Route" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All routes</SelectItem>
              {routes.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={direction}
            onValueChange={(value) => setDirection(typeof value === 'string' ? value : 'all')}
            items={[{ value: 'all', label: 'All directions' }, ...directions.map((value) => ({ value, label: value }))]}
          >
            <SelectTrigger aria-label="Direction">
              <SelectValue placeholder="Direction" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All directions</SelectItem>
              {directions.map((value) => (
                <SelectItem key={value} value={value}>
                  {value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={stop}
            onValueChange={(value) => setStop(typeof value === 'string' ? value : 'all')}
            items={[
              { value: 'all', label: 'All stops' },
              ...stops.map((value) => ({
                value,
                label: entries.find((entry) => entry.stopId === value)?.stopName ?? value,
              })),
            ]}
          >
            <SelectTrigger aria-label="Stop">
              <SelectValue placeholder="Stop" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stops</SelectItem>
              {stops.map((value) => (
                <SelectItem key={value} value={value}>
                  {entries.find((entry) => entry.stopId === value)?.stopName ?? value}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={serviceDay}
            onValueChange={(value) => setServiceDay(typeof value === 'string' ? value : 'all')}
            items={[
              { value: 'all', label: 'All service days' },
              ...serviceDays.map((value) => ({ value, label: displayDay(value) })),
            ]}
          >
            <SelectTrigger aria-label="Service day">
              <SelectValue placeholder="Service day" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All service days</SelectItem>
              {serviceDays.map((value) => (
                <SelectItem key={value} value={value}>
                  {displayDay(value)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}
      {lastUpdatedAt && (
        <p className="text-sm text-muted-foreground">Last documented update {displayTime(lastUpdatedAt)}</p>
      )}
      {!selected.length ? (
        <div className="rounded-xl border border-border bg-card p-8 text-center text-sm text-muted-foreground">
          No more documented departures match this stop and service day.
        </div>
      ) : (
        <div className="relative space-y-3 before:absolute before:top-5 before:bottom-5 before:left-5 before:w-px before:bg-border">
          {selected.map((entry, index) => (
            <Card
              key={`${entry.tripId ?? 'trip'}-${entry.scheduledArrival ?? index}`}
              className="relative ml-3 border-border/80"
            >
              <CardHeader className="pb-2 pl-8">
                <CardTitle className="flex items-center gap-3">
                  <span className="inline-flex min-w-14 items-center justify-center rounded-full bg-primary px-3 py-1 text-lg font-semibold text-primary-foreground">
                    {entry.route ?? '—'}
                  </span>
                  <span>{entry.stopName ?? 'Unnamed stop'}</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-2 pl-8 text-sm text-muted-foreground sm:grid-cols-[auto_1fr_auto]">
                <span className="flex items-center gap-2 font-medium text-foreground">
                  <Clock3 className="size-4 text-primary" />
                  {displayTime(entry.scheduledArrival)}{' '}
                  <span className="text-muted-foreground">· {minutesAway(entry.scheduledArrival)}</span>
                </span>
                <span className="flex items-center gap-2">
                  <MapPin className="size-4" />
                  {entry.direction ?? 'Direction not documented'} ·{' '}
                  {entry.destinationLabel ?? 'Destination not documented'}
                </span>
                <span className="flex items-center gap-2 sm:justify-self-end">
                  <RouteIcon className="size-4" />
                  {displayDay(entry.serviceDay)}
                </span>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </section>
  );
}
